import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useArtifactGate, GateLock } from './ArtifactGate';
import { fetchFlattenedPreview, FlattenedPreviewError } from '../services/FlattenedPreviewService';

/**
 * The visitor sees their completed form. They do not receive it.
 *
 * <h2>Why this is images and not a PDF</h2>
 * Design section 3.3.1. If the server sent the flattened PDF for the browser to
 * display, the file would already be on the client - the browser's own viewer
 * offers a save button and the network tab offers the bytes. So the server
 * rasterises and sends pixels: no text layer, no fields, no vectors. Right-click
 * yields an image, which is exactly the "reasonably prevented" bar.
 *
 * Deliberately no right-click blocking or overlay tricks. The design says not to
 * over-engineer this: rasterisation already defeats casual extraction, and
 * someone determined enough to rebuild a form from page images was never a sale.
 * Blocking the context menu only annoys the honest majority.
 *
 * <h2>Why the download button lives here</h2>
 * This is the moment the product has just proved itself, which section 3.3.1
 * names as where the upgrade prompt attaches naturally. The button is real for
 * everyone; for a guest, `guard` opens the standard gate with the agreed copy -
 * "Your completed PDF is ready. Create an account to download it." - rather
 * than this component inventing its own wording.
 */
interface FlattenedPreviewModalProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
  /** Values currently entered in the Live Form Preview. */
  formData: any;
  /** Builds the request body; shared with the download so the two cannot drift. */
  buildFormConfiguration: (formData: any) => unknown | null;
  licenseKeyHash?: string | null;
  /** The real download, run only if the session is allowed to have it. */
  onDownload: () => void;
}

const FlattenedPreviewModal: React.FC<FlattenedPreviewModalProps> = ({
  open,
  onClose,
  documentId,
  formData,
  buildFormConfiguration,
  licenseKeyHash,
  onDownload,
}) => {
  const { guard, allows } = useArtifactGate();
  const [pages, setPages] = useState<string[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const load = useCallback(async () => {
    const configuration = buildFormConfiguration(formData);
    if (!configuration) {
      setError('No document uploaded. Please upload a PDF first.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const preview = await fetchFlattenedPreview(documentId, configuration, licenseKeyHash);
      setPages(preview.pages);
      setTotalPages(preview.totalPages);
      if (preview.pages.length === 0) {
        setError('That form produced no pages to show.');
      }
    } catch (e) {
      // Show the server's own words. A refusal here carries the reason, and a
      // generic "something went wrong" would discard it.
      setError(
        e instanceof FlattenedPreviewError
          ? e.message
          : 'Could not render the preview. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [buildFormConfiguration, formData, documentId, licenseKeyHash]);

  useEffect(() => {
    if (open) {
      load();
      closeButtonRef.current?.focus();
    } else {
      setPages([]);
      setTotalPages(0);
      setError(null);
    }
    // `load` is intentionally omitted: it changes identity with every keystroke
    // in the form, and re-rendering the preview on each one would hammer an
    // endpoint that runs a full flatten. The preview is a snapshot of the
    // moment it was opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const truncated = totalPages > pages.length && pages.length > 0;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="flattened-preview-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <h2 id="flattened-preview-title" className="text-lg font-semibold text-neutral-900">
              Your completed PDF
            </h2>
            <p className="text-sm text-neutral-500 mt-0.5">
              This is exactly how the flattened PDF will look.
              {truncated && ` Showing the first ${pages.length} of ${totalPages} pages.`}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Close preview"
            className="text-neutral-400 hover:text-neutral-600 transition-colors text-xl leading-none px-2"
          >
            &times;
          </button>
        </div>

        {/* min-h-0 is load-bearing. A flex child defaults to min-height:auto,
            so this refused to shrink below its content - the rendered pages -
            and the panel grew past max-h-[90vh], carrying the header and its
            close button off the top of the screen. The images scrolled; the
            way out did not. */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 bg-neutral-50">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 text-neutral-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-3"></div>
              <p className="text-sm">Flattening your form&hellip;</p>
            </div>
          )}

          {!loading && error && (
            <div className="py-12 text-center">
              <p className="text-sm text-neutral-700 mb-4">{error}</p>
              <button
                onClick={load}
                className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
              >
                Try again
              </button>
            </div>
          )}

          {!loading && !error && pages.map((page, index) => (
            <img
              key={index}
              src={`data:image/png;base64,${page}`}
              alt={`Page ${index + 1} of your completed form`}
              className="w-full mb-4 last:mb-0 shadow-sm border border-neutral-200 bg-white"
            />
          ))}
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between gap-4">
          <p className="text-xs text-neutral-500">
            Preview only &mdash; the PDF itself stays on our server until it&rsquo;s yours.
          </p>
          <button
            type="button"
            onClick={() => guard('flattened', onDownload)}
            disabled={loading || !!error}
            className={`flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
              loading || error
                ? 'bg-neutral-300 text-white cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md'
            }`}
          >
            Download this PDF
            <GateLock show={!allows('flattened')} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlattenedPreviewModal;
